import { useMemo, type ReactElement } from 'react';
import { create } from 'qrcode';

/** The quiet zone the QR spec asks for around the symbol, in modules. */
const QUIET = 4;

/**
 * `W4-T09` §2.1 — the QR the client points their own camera at.
 *
 * Drawn from `qrcode`'s module matrix as one SVG path, synchronously and with no network: no canvas,
 * no data URL, no `innerHTML`. Black on white whatever the theme, because a camera reads contrast,
 * not tokens. Error correction `M`, the library's default, survives a cracked screen protector.
 *
 * `value` is the issue response's `url`, which carries the token in its fragment — so it is
 * rendered and never logged, and the short code beside it is the path for anyone without a camera.
 */
export function HandshakeQr({ value, label }: { value: string; label: string }): ReactElement {
  const { size, path } = useMemo(() => {
    const { modules } = create(value, { errorCorrectionLevel: 'M' });
    let d = '';
    for (let row = 0; row < modules.size; row += 1) {
      for (let col = 0; col < modules.size; col += 1) {
        if (modules.get(row, col)) d += `M${col + QUIET} ${row + QUIET}h1v1h-1z`;
      }
    }
    return { size: modules.size + QUIET * 2, path: d };
  }, [value]);

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${size} ${size}`}
      width="256"
      height="256"
      shapeRendering="crispEdges"
      data-testid="handshake-qr"
    >
      <rect width={size} height={size} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
}
